import {
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

const DROPDOWN_MAX_HEIGHT = 200;
const DROPDOWN_MIN_HEIGHT = 80;
const DROPDOWN_VIEWPORT_MARGIN = 8;
/** Longer than the search debounce (300 ms) — see `restoreSelectionOnBlur`. */
const RESTORE_AFTER_BLUR_MS = 350;

@Directive({
    selector: '[hamSimpleDropDown]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: HamSimpleDropDownDirective,
            multi: true,
        },
    ],
})
export class HamSimpleDropDownDirective implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
    @Input() label: string = ''; // Added label input
    @Input() options: any[] = []; // Input options for autocomplete, now accepting any type
    @Input() optionKeys: string[] = []; // Keys of the object to display and search by
    @Input() extraLabel: string[] = []; // Keys of the object to display extra information in the dropdown
    @Input() returnKey: string = ''; // Key of the object to return to the form control
    @Input() multiple: boolean = false; // New input to enable multi-select mode
    @Input() returnObject: boolean = false; // New input to return the selected object instead of the key
    @Input() hideDisplaySelection: boolean = false; // New input to return the selected object instead of the key
    @Input() showRequired: boolean = false; // add indication for required field
    @Input() draggable = false;
    @Input() badgeStyleDropdown = false;
    @Input() allowFreeText: boolean = true;
    /**
     * Opt-in, single select: on leaving the field, its text shows the value it is actually bound to
     * again. Without it, text typed but never picked stays on screen while the bound value is the
     * previous pick. Search stays as it is (unlike `allowFreeText = false`).
     */
    @Input() restoreSelectionOnBlur = false;

    private _disabled: boolean = false;
    @Input() set disabled(value: boolean) {
        this._disabled = value;
        this.setDisabledState(value);
    }
    get disabled(): boolean {
        return this._disabled;
    }
    @Input() selectedOptions: any[] = []; // Store selected options for multi-select mode

    private wrapperElement: HTMLElement;
    private labelElement: HTMLElement; // Reference to the label element
    private inputWrapperElement: HTMLElement;
    private dropdownElement: HTMLElement;
    private iconWrapper: HTMLElement;
    private activeIndex: number = 0; 
    private clearButton: HTMLElement;
    private badgeOverlay: HTMLElement; // Overlay span for badge display
    private filteredList: any[] = [];  

    private isDropdownVisible: boolean = false;
    /** Side the list opened to, kept for as long as the dropdown stays open. null = not decided yet. */
    private openUpwards: boolean | null = null;
    private selectedItemsElement: HTMLElement;

    private listeners: (() => void)[] = [];
    private onChange: (value: any) => void = () => { };
    private onTouched: () => void = () => { };
    private _writtenValue: any;
    /** The value last handed to the form — written, picked or cleared. */
    private _committedValue: any = null;
    @Input() formControl: FormControl;
    // Search buffer for autocomplete
    private searchBuffer: string = '';
    private searchTimeout: any;
    private searchSub: Subscription;
    private observer: IntersectionObserver;
    private boundOnWindowScroll: EventListener;

    /** When true: tracks that this is a zefix field — blue background + any selection will add yellow styling and emit zefixModified. */
    @Input() set zefixTracked(value: boolean) {
        this._zefixTracked = value;
        if (this.inputWrapperElement) {
            if (value) {
                this.renderer.addClass(this.inputWrapperElement, 'zefix-tracked');
            } else {
                this.renderer.removeClass(this.inputWrapperElement, 'zefix-tracked');
            }
        }
    }
    private _zefixTracked = false;
    /** When true: orange/yellow styling indicating the field was originally from Zefix but manually overridden. */
    @Input() set zefixOverridden(value: boolean) {
        this._zefixOverridden = value;
        if (this.inputWrapperElement) {
            if (value) {
                this.renderer.addClass(this.inputWrapperElement, 'zefix-overridden');
            } else {
                this.renderer.removeClass(this.inputWrapperElement, 'zefix-overridden');
                // Also clear the zefix-modified class so re-syncing from diff fully restores the blue state
                this.renderer.removeClass(this.inputWrapperElement, 'zefix-modified');
            }
        }
    }
    private _zefixOverridden = false;
    /** Emitted when the user selects a new option on a zefix-tracked field. */
    @Output() zefixModified = new EventEmitter<void>();

    @Output() selectionChange: EventEmitter<any> = new EventEmitter();
    @Output() clear = new EventEmitter<void>();

    constructor(private el: ElementRef, private renderer: Renderer2) {
        this.boundOnWindowScroll = this.onWindowScroll.bind(this);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['options']) {
            if (this._writtenValue) {
                if (this.multiple && Array.isArray(this._writtenValue)) {
                    this.selectedOptions = [...this._writtenValue];
                    this.updateSelectedItems();
                } else {
                    this.updateSelectDisplay(this._writtenValue);
                }
            }
        }
        if (changes['zefixTracked'] && this.inputWrapperElement) {
            if (this._zefixTracked) {
                this.renderer.addClass(this.inputWrapperElement, 'zefix-tracked');
            } else {
                this.renderer.removeClass(this.inputWrapperElement, 'zefix-tracked');
            }
        }
        if (changes['zefixOverridden'] && this.inputWrapperElement) {
            if (this._zefixOverridden) {
                this.renderer.addClass(this.inputWrapperElement, 'zefix-overridden');
            } else {
                this.renderer.removeClass(this.inputWrapperElement, 'zefix-overridden');
                // Also clear the zefix-modified class so re-syncing from diff fully restores the blue state
                this.renderer.removeClass(this.inputWrapperElement, 'zefix-modified');
            }
        }
    }

    ngOnInit() {
        this.createWrapper();
        this.createLabel(); // Create the label
        this.createInputWrapper();
        this.createDropdown();
        this.createChevronIcon();

        if (this.badgeStyleDropdown) {
            this.createBadgeOverlay();
        }

        if (!this._disabled) {
            this.createClearButton();
        }
        if (this.multiple) {
            this.updateSelectedItems();
        }

        if (this.formControl && !this._disabled) {
        if (this.formControl.value) {
                this.renderer.setStyle(this.clearButton, 'display', 'block');
            } else {
                this.renderer.setStyle(this.clearButton, 'display', 'none');
            }
        }

        // Only subscribe to input events when free text is allowed
        if (this.allowFreeText) {
            const input$ = fromEvent(this.el.nativeElement, 'input').pipe(debounceTime(300));
            this.searchSub = input$.subscribe(() => {
                this.onInput();
            });
        } else {
            // Prevent pasting arbitrary values when free text is not allowed
            this.listeners.push(
                this.renderer.listen(this.el.nativeElement, 'paste', (event: ClipboardEvent) => {
                    event.preventDefault();
                }),
            );
        }

        // Keyboard events on the input
        this.listeners.push(
            this.renderer.listen(this.el.nativeElement, 'keydown', (event: KeyboardEvent) => this.onKeyDown(event)),
        );

        if (this.selectedOptions.length > 0) {
            this.updateSelectedItems();
        }

        // Click outside: listener is registered only while the dropdown is open (see openOutsideClickListener)

        // Use IntersectionObserver to detect visibility
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting && this.isDropdownVisible) {
                    this.closeDropdown();
                }
            });
        });

        // Listen for focus events to behave like click
        this.listeners.push(
            this.renderer.listen(this.el.nativeElement, 'focus', (event) => {
                if (!this._disabled && !this.isDropdownVisible) {
                    this.toggleDropdown();
                }
            }),
        );

        // Listen for click events
        this.listeners.push(
            this.renderer.listen(this.el.nativeElement, 'click', (event) => {
                event.stopPropagation();
                if (!this._disabled && !this.isDropdownVisible) {
                    this.toggleDropdown();
                }
            }),
        );

        this.listeners.push(
            this.renderer.listen(this.el.nativeElement, 'blur', (event) => {
                // Add a small delay to allow click events on dropdown items to fire first
                setTimeout(() => {
                    if (this.isDropdownVisible && !this.wrapperElement.contains(event.relatedTarget as Node)) {
                        this.closeDropdown();
                    }
                }, 150);
                if (this.restoreSelectionOnBlur && !this.multiple) {
                    // After the 300 ms search debounce, so a search still pending cannot reopen the list
                    // with the typed text once the display is back.
                    setTimeout(() => this.restoreCommittedDisplay(), RESTORE_AFTER_BLUR_MS);
                }
            }),
        );

        // Make the dropdown focusable
        this.renderer.setAttribute(this.dropdownElement, 'tabindex', '0');

        // Add keyboard navigation to the dropdown itself
        this.listeners.push(
            this.renderer.listen(this.dropdownElement, 'keydown', (event) => {
                this.onDropdownKeyDown(event);
            }),
        );

        this.observer.observe(this.el.nativeElement);

        if (this._zefixTracked) {
            this.renderer.addClass(this.inputWrapperElement, 'zefix-tracked');
        }

        if (this._zefixOverridden) {
            this.renderer.addClass(this.inputWrapperElement, 'zefix-overridden');
        }
    }

    private getScrollableParents(element: HTMLElement): HTMLElement[] {
        const scrollableParents: HTMLElement[] = [];
        let parent = element.parentElement;

        while (parent) {
            const overflowY = window.getComputedStyle(parent).overflowY;
            if (overflowY === 'scroll' || overflowY === 'auto') {
                scrollableParents.push(parent);
            }
            parent = parent.parentElement;
        }

        // Optionally add the window if the document is scrollable
        if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
            scrollableParents.push(window as unknown as HTMLElement);
        }

        return scrollableParents;
    }

    // Add this new method to handle dropdown-specific keyboard events
    private onDropdownKeyDown(event: KeyboardEvent) {
        const options = this.dropdownElement.children;
        const optionsLength = options.length;

        // Initialize activeIndex if not set
        if (this.activeIndex === -1 && optionsLength > 0) {
            this.activeIndex = 0;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault(); // Prevent default scrolling
            if (this.activeIndex < optionsLength - 1) {
                this.activeIndex++;
            } else {
                this.activeIndex = 0; // Loop to first option
            }
            this.highlightOption();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault(); // Prevent default scrolling
            if (this.activeIndex > 0) {
                this.activeIndex--;
            } else {
                this.activeIndex = optionsLength - 1; // Loop to last option
            }
            this.highlightOption();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (this.activeIndex >= 0 && this.activeIndex < optionsLength) {
                const option = this.filteredList[this.activeIndex];
                this.onSelectOption(option);
            }
        } else if (event.key === 'Escape') {
            event.preventDefault();
            this.closeDropdown();
            this.el.nativeElement.focus(); // Return focus to input
        } else if (event.key === 'Tab') {
            // Allow tab to work normally to move focus away
            this.closeDropdown();
        }
    }

    private onWindowScroll = () => {
        if (this.isDropdownVisible) {
            this.updateDropdownPosition(true);
        }
    };

    private outsideClickUnlisten: (() => void) | null = null;

    private openOutsideClickListener(): void {
        if (this.outsideClickUnlisten) return;
        this.outsideClickUnlisten = this.renderer.listen('document', 'click', (event) => {
            if (this.isDropdownVisible && !this.wrapperElement.contains(event.target as Node)) {
                this.closeDropdown();
            }
        });
    }

    private closeOutsideClickListener(): void {
        this.outsideClickUnlisten?.();
        this.outsideClickUnlisten = null;
    }

    ngOnDestroy() {
        this.closeOutsideClickListener();
        this.listeners.forEach((listener) => listener());
        if (this.searchSub) {
            this.searchSub.unsubscribe();
        }
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    private createLabel() {
        if (!this.label) return; // Skip if no label is provided

        this.labelElement = this.renderer.createElement('label');
        const labelText = this.renderer.createText(this.label);
        this.renderer.appendChild(this.labelElement, labelText);
        this.renderer.appendChild(this.wrapperElement, this.labelElement);
        // Optionally, associate the label with the input
        const inputId = `ham-simple-dropdown-${Math.random().toString(36).substr(2, 9)}`;
        this.renderer.setAttribute(this.el.nativeElement, 'id', inputId);
        this.renderer.setAttribute(this.labelElement, 'for', inputId);

        // Add any necessary classes for styling
        this.renderer.addClass(this.labelElement, 'ham-dropdown-label');
        if (this.showRequired) {
            this.renderer.addClass(this.labelElement, 'required');
        }
    }

    private createChevronIcon() {
        this.iconWrapper = this.renderer.createElement('span');
        this.renderer.addClass(this.iconWrapper, 'ham-icon');
        this.renderer.appendChild(this.inputWrapperElement, this.iconWrapper);

        const chevronIcon = this.renderer.createElement('i');
        this.renderer.addClass(chevronIcon, 'gray-chevron-down-icon');
        this.renderer.appendChild(this.iconWrapper, chevronIcon);

        this.renderer.setStyle(this.iconWrapper, 'position', 'absolute');
        this.renderer.setStyle(this.iconWrapper, 'top', '20px');
        this.renderer.setStyle(this.iconWrapper, 'right', '16px');
        this.renderer.setStyle(this.iconWrapper, 'transform', 'translateY(-50%)');
        this.renderer.setStyle(this.iconWrapper, 'cursor', 'pointer');

        // Add click listener to toggle dropdown visibility
        this.listeners.push(
            this.renderer.listen(this.iconWrapper, 'click', (event) => {
                event.stopPropagation();
                if (!this._disabled) {
                    this.toggleDropdown();
                }
            }),
        );
    }

    private createClearButton() {
        this.clearButton = this.renderer.createElement('span');
        this.renderer.addClass(this.clearButton, 'ham-clear-button');
        this.renderer.addClass(this.clearButton, 'ham-icon');
        this.renderer.setStyle(this.clearButton, 'position', 'absolute');
        this.renderer.setStyle(this.clearButton, 'right', '40px'); // Adjust position as needed

        this.renderer.setStyle(this.clearButton, 'top', '20px');

        this.renderer.setStyle(this.clearButton, 'transform', 'translateY(-50%)');
        this.renderer.setStyle(this.clearButton, 'cursor', 'pointer');
        this.renderer.setStyle(this.clearButton, 'display', 'none'); // Initially hidden

        const clearIcon = this.renderer.createElement('i');
        this.renderer.addClass(clearIcon, 'x-icon'); // Ensure you have appropriate CSS for 'x-icon'
        this.renderer.setStyle(clearIcon, 'font-size', '10px'); // Adjust size as needed
        this.renderer.appendChild(this.clearButton, clearIcon);

        this.renderer.appendChild(this.inputWrapperElement, this.clearButton);

        // Add click listener to clear selection
        this.listeners.push(
            this.renderer.listen(this.clearButton, 'click', (event) => {
                event.stopPropagation(); // Prevent triggering other click events
                this.clearSelection();
            }),
        );
    }

    private createBadgeOverlay() {
        this.badgeOverlay = this.renderer.createElement('span');
        this.renderer.addClass(this.badgeOverlay, 'badge-overlay');
        this.renderer.setStyle(this.badgeOverlay, 'position', 'absolute');
        this.renderer.setStyle(this.badgeOverlay, 'top', '50%');
        this.renderer.setStyle(this.badgeOverlay, 'left', '16px');
        this.renderer.setStyle(this.badgeOverlay, 'transform', 'translateY(-50%)');
        this.renderer.setStyle(this.badgeOverlay, 'display', 'none');
        this.renderer.setStyle(this.badgeOverlay, 'pointer-events', 'none');
        this.renderer.setStyle(this.badgeOverlay, 'z-index', '10');

        this.renderer.appendChild(this.inputWrapperElement, this.badgeOverlay);

        // Make input readonly and remove cursor in badge mode
        this.renderer.setAttribute(this.el.nativeElement, 'readonly', 'true');
        this.renderer.setStyle(this.el.nativeElement, 'cursor', 'pointer');
    }

    private updateBadgeOverlay(text: string) {
        if (!this.badgeStyleDropdown || !this.badgeOverlay) {
            return;
        }

        // Clear existing content
        while (this.badgeOverlay.firstChild) {
            this.badgeOverlay.removeChild(this.badgeOverlay.firstChild);
        }

        if (text) {
            const badgeText = this.renderer.createText(text);
            this.renderer.appendChild(this.badgeOverlay, badgeText);
            // Badge always visible when there's text
            this.renderer.setStyle(this.badgeOverlay, 'display', 'block');
        } else {
            // Only hide when there's no text
            this.renderer.setStyle(this.badgeOverlay, 'display', 'none');
        }
    }

    writeValue(value: any): void {
        this._writtenValue = value;
        this._committedValue = value;
        if (this.multiple && Array.isArray(value)) {
            this.selectedOptions = [...value];
            this.updateSelectedItems();
        } else {
            this.updateSelectDisplay(value);
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this._disabled = isDisabled;
        this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
        this.renderer.setProperty(this.el.nativeElement, 'tabIndex', isDisabled ? -1 : 0);
        this.renderer.setStyle(this.el.nativeElement, 'color', isDisabled ? '#999' : '');
        this.renderer.setStyle(this.el.nativeElement, 'background-color', isDisabled ? '#f5f5f5' : '');

        if (this.clearButton) {
            this.renderer.setStyle(this.clearButton, 'display', isDisabled ? 'none' : null);
        }
        if (this.iconWrapper) {
            this.renderer.setStyle(this.iconWrapper, 'color', isDisabled ? '#999' : '');
            this.renderer.setStyle(this.iconWrapper, 'pointer-events', isDisabled ? 'none' : 'auto');
        }
        if (this.wrapperElement) {
            this.renderer.setStyle(this.wrapperElement, 'opacity', isDisabled ? '0.6' : '1');
        }

        // Close dropdown if it's open and component is being disabled
        if (isDisabled && this.isDropdownVisible) {
            this.closeDropdown();
        }
    }

    /** Shows the bound value again — unless the focus came back into the field or its list. */
    private restoreCommittedDisplay() {
        if (this.wrapperElement.contains(document.activeElement)) {
            return;
        }
        if (this.isDropdownVisible) {
            this.closeDropdown();
        }
        this.updateSelectDisplay(this._committedValue);
        const hasValue = this._committedValue != null && this._committedValue !== '';
        this.renderer.setStyle(this.clearButton, 'display', hasValue && !this._disabled ? 'block' : 'none');
    }

    private clearSelection() {
        this._committedValue = this.multiple ? [] : null;
        this.selectedOptions = [];
        this.updateDisplay();
        this.updateSelectedItems();
        this.renderer.setProperty(this.el.nativeElement, 'value', '');

        // Hide badge overlay when cleared
        if (this.badgeStyleDropdown && this.badgeOverlay) {
            this.updateBadgeOverlay('');
        }

        // Hide the clear button since there's nothing to clear
        this.renderer.setStyle(this.clearButton, 'display', 'none');

        // Optionally, close the dropdown if it's open
        if (this.isDropdownVisible) {
            this.closeDropdown();
        }

        this.onChange(this.multiple ? [] : null);
        this.onTouched();
        this.clear.emit();
    }

    private updateSelectDisplay(value: any) {
        if (value) {
            const filter = typeof value === 'object' ? value[this.returnKey] : value;
            let matchingOption = this.options.find((option) => option[this.returnKey] === filter);
            if (matchingOption == null) {
                //IF didn't find a matching option, try to find a close one
                matchingOption = this.options.find((option) => typeof option[this.returnKey] === 'string' && option[this.returnKey].startsWith(filter));
            }
            if (matchingOption) {
                const mainText = this.optionKeys
                    .map((key) => getNestedValue(matchingOption, key))
                    .filter(Boolean)
                    .join(' - ');

                const extraText =
                    this.extraLabel.length > 0
                        ? this.extraLabel
                            .map((key) => getNestedValue(matchingOption, key))
                            .filter(Boolean)
                            .join(' - ')
                        : '';

                const displayText = extraText ? `${mainText} (${extraText})` : mainText;
                this.renderer.setProperty(this.el.nativeElement, 'value', displayText);

                // Update badge overlay if badgeStyleDropdown is enabled and input is not focused
                if (this.badgeStyleDropdown && document.activeElement !== this.el.nativeElement) {
                    this.updateBadgeOverlay(displayText);
                }
            } else if (typeof value === 'object') {
                this.renderer.setProperty(this.el.nativeElement, 'value', '');
                if (this.badgeStyleDropdown) {
                    this.updateBadgeOverlay('');
                }
            } else {
                this.renderer.setProperty(this.el.nativeElement, 'value', value || '');
                if (this.badgeStyleDropdown) {
                    this.updateBadgeOverlay(value || '');
                }
            }
        } else {
            this.renderer.setProperty(this.el.nativeElement, 'value', '');
            if (this.badgeStyleDropdown) {
                this.updateBadgeOverlay('');
            }
        }
    }

    private createWrapper() {
        this.wrapperElement = this.renderer.createElement('div');
        this.renderer.insertBefore(this.el.nativeElement.parentNode, this.wrapperElement, this.el.nativeElement);
        this.renderer.addClass(this.wrapperElement, 'ham-drop-down');
    }

    private createInputWrapper() {
        this.inputWrapperElement = this.renderer.createElement('div');
        if (this.badgeStyleDropdown) {
            this.renderer.addClass(this.inputWrapperElement, 'badge-style');
        }
        this.renderer.appendChild(this.wrapperElement, this.inputWrapperElement);
        this.renderer.appendChild(this.inputWrapperElement, this.el.nativeElement);
        this.renderer.setStyle(this.inputWrapperElement, 'position', 'relative');
        this.renderer.addClass(this.inputWrapperElement, 'input-wrapper');
    }

    private createDropdown() {
        this.dropdownElement = this.renderer.createElement('ul');
        this.renderer.addClass(this.dropdownElement, 'autocomplete-dropdown');
        if (this.badgeStyleDropdown) {
            this.renderer.addClass(this.dropdownElement, 'badge-style');
        }
        this.renderer.setStyle(this.dropdownElement, 'position', 'fixed');
        this.renderer.setStyle(this.dropdownElement, 'top', '0');
        this.renderer.setStyle(this.dropdownElement, 'left', '0');
        this.renderer.setStyle(this.dropdownElement, 'right', '0');
        this.renderer.setStyle(this.dropdownElement, 'border', '1px solid #ccc');
        this.renderer.setStyle(this.dropdownElement, 'background-color', '#fff');
        this.renderer.setStyle(this.dropdownElement, 'z-index', '99999');
        this.renderer.setStyle(this.dropdownElement, 'max-height', `${DROPDOWN_MAX_HEIGHT}px`);
        this.renderer.setStyle(this.dropdownElement, 'overflow-y', 'auto');
        this.renderer.setStyle(this.dropdownElement, 'display', 'none');

        this.listeners.push(
            this.renderer.listen(this.dropdownElement, 'click', (event) => {
                event.stopPropagation();
            }),
        );

        this.renderer.appendChild(this.inputWrapperElement, this.dropdownElement);

        const selectedItemsElement = this.renderer.createElement('ul');
        this.renderer.addClass(selectedItemsElement, 'selectedItems');
        this.renderer.setStyle(selectedItemsElement, 'display', 'none');
        this.renderer.appendChild(this.inputWrapperElement, selectedItemsElement);

        this.selectedItemsElement = selectedItemsElement;
    }

    private onInput() {
        const value = this.el.nativeElement.value ?? '';
        const trimmed = value.trim();

        // --- NEW: clear selection if input is emptied by the user ---
        if (trimmed === '') {
            const hadSelection =
                (this.multiple && this.selectedOptions.length > 0) ||
                (!this.multiple && (this._writtenValue != null && this._writtenValue !== '')) ||
                (!!this.formControl && this.formControl.value != null && this.formControl.value !== '' && !(Array.isArray(this.formControl.value) && this.formControl.value.length === 0));

            if (hadSelection) {
                this.clearSelection(); // triggers same events as clearButton click
                return;                // we're done (clearSelection hides dropdown & button)
            }
        }
        // ------------------------------------------------------------
        // Show or hide the clear button
        if (trimmed) {
            this.renderer.setStyle(this.clearButton, 'display', 'block');
        } else {
            this.renderer.setStyle(this.clearButton, 'display', 'none');
        }
        if (trimmed) {
            this.isDropdownVisible = true;
            this.openOutsideClickListener();
            this.renderer.setStyle(this.dropdownElement, 'display', 'block');
            // Handle search buffer
            this.activeIndex = 0; 
            const filteredOptions = this.options.filter((option) =>
                this.optionKeys.some((key) => getNestedValue(option, key)?.toLowerCase().includes(trimmed.toLowerCase())),
            );
            this.filteredList = filteredOptions; // Store the filtered list for keyboard navigation
            this.updateDropdown(filteredOptions);
        } else {
            this.searchBuffer = ''; // Reset search buffer
            this.activeIndex = 0; // Reset active index
            this.updateDropdown(this.options); // Show all options
            // Close through closeDropdown() so the outside-click and scroll listeners are released too
            this.closeDropdown();
        }
    }

    private updateDropdown(options: any[]) {
        while (this.dropdownElement.firstChild) {
            this.dropdownElement.removeChild(this.dropdownElement.firstChild);
        }

        options.forEach((option, index) => {
            const optionElement = this.renderer.createElement('li');
            this.renderer.setStyle(optionElement, 'text-align', 'left');
            this.renderer.setStyle(optionElement, 'padding', '8px 12px');
            this.renderer.setStyle(optionElement, 'cursor', 'pointer');
            this.renderer.setStyle(optionElement, 'white-space', 'nowrap');
            this.renderer.setStyle(optionElement, 'overflow', 'hidden');
            this.renderer.setStyle(optionElement, 'text-overflow', 'ellipsis');

            const mainText = this.optionKeys
                .map((key) => getNestedValue(option, key))
                .filter(Boolean)
                .join(' - ');

            const extraText =
                this.extraLabel.length > 0
                    ? this.extraLabel
                        .map((key) => getNestedValue(option, key))
                        .filter(Boolean)
                        .join(' - ')
                    : '';

            const displayText = extraText ? `${mainText} (${extraText})` : mainText;
            const optionText = this.renderer.createText(displayText);
            this.renderer.appendChild(optionElement, optionText);
            this.listeners.push(this.renderer.listen(optionElement, 'click', () => this.onSelectOption(option)));
            this.renderer.appendChild(this.dropdownElement, optionElement);

            if (this.draggable) {
                this.addDragAndDropListeners(optionElement, option);
            }

            if (index === this.activeIndex) {
                this.renderer.addClass(optionElement, 'active');
                this.renderer.setStyle(optionElement, 'background-color', '#ecfffe');
                // Scroll to the active element
                optionElement.scrollIntoView({ block: 'nearest' });
            } else {
                this.renderer.setStyle(optionElement, 'background-color', '#fff');
            }
        });

        if (options.length > 0) {
            this.renderer.setStyle(this.dropdownElement, 'display', 'block');
            this.updateDropdownPosition();
        } else {
            this.renderer.setStyle(this.dropdownElement, 'display', 'none');
        }
    }

    private toggleDropdown() {
        if (this._disabled) {
            return;
        }

        this.isDropdownVisible = !this.isDropdownVisible;

        if (this.isDropdownVisible) {
            this.openOutsideClickListener();
            this.openUpwards = null; 
            this.filteredList = this.options;
            this.activeIndex = 0;
            const value = this.el.nativeElement.value.trim().toLowerCase();
            if (value) {
                this.updateDropdown(this.options);
                this.highlightFirstMatching(value);
            } else {
                this.updateDropdown(this.options);
                this.highlightOption(); 
            }
            this.renderer.setStyle(this.dropdownElement, 'display', 'block');
            this.updateDropdownPosition(true);
            this.el.nativeElement.focus(); // Focus on input when dropdown is opened

            const scrollableParents = this.getScrollableParents(this.el.nativeElement);
            // Add scroll listeners to all scrollable parents
            scrollableParents.forEach((parent) => {
                parent.addEventListener('scroll', this.boundOnWindowScroll);
            });
            if (this.multiple) {
                this.highlightSelectedOptions();
            }
        } else {
            this.closeDropdown();
        }
    }

    /**
     * @param recomputeSide when true the above/below decision is taken fresh; otherwise the side
     * chosen when the dropdown was opened is kept, so filtering does not make the list jump sides.
     */
    private updateDropdownPosition(recomputeSide = false) {
        const rect = this.el.nativeElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        const spaceBelow = viewportHeight - rect.bottom - DROPDOWN_VIEWPORT_MARGIN;
        const spaceAbove = rect.top - DROPDOWN_VIEWPORT_MARGIN;

        // Measure the natural height of the CURRENT (possibly filtered) option list, so the
        // height and the flip decision always match what is actually rendered.
        this.renderer.setStyle(this.dropdownElement, 'max-height', 'none');
        const desiredHeight = Math.min(this.dropdownElement.offsetHeight, DROPDOWN_MAX_HEIGHT);

        // Prefer below; only flip above when the list does not fit below and there is more room above.
        if (recomputeSide || this.openUpwards == null) {
            this.openUpwards = desiredHeight > spaceBelow && spaceAbove > spaceBelow;
        }
        const openUpwards = this.openUpwards;
        const availableHeight = openUpwards ? spaceAbove : spaceBelow;

        // Shrink (and scroll) instead of overflowing the viewport, but never collapse to nothing.
        const height = Math.min(desiredHeight, Math.max(availableHeight, DROPDOWN_MIN_HEIGHT));
        const top = openUpwards ? Math.max(DROPDOWN_VIEWPORT_MARGIN, rect.top - height) : rect.bottom;

        this.renderer.setStyle(this.dropdownElement, 'max-height', `${height}px`);
        this.renderer.setStyle(this.dropdownElement, 'top', `${top}px`);
        this.renderer.setStyle(this.dropdownElement, 'left', `${rect.left}px`);
        this.renderer.setStyle(this.dropdownElement, 'width', `${rect.width}px`);
    }

    private closeDropdown() {
        this.isDropdownVisible = false;
        this.closeOutsideClickListener();
        this.renderer.setStyle(this.dropdownElement, 'display', 'none');
        this.openUpwards = null;
        this.activeIndex = 0;
        const scrollableParents = this.getScrollableParents(this.el.nativeElement);
        // Add scroll listeners to all scrollable parents
        scrollableParents.forEach((parent) => {
            parent.removeEventListener('scroll', this.boundOnWindowScroll);
        });
    }

    private onKeyDown(event: KeyboardEvent) {
        if (this._disabled) {
            return;
        }

        const options = this.dropdownElement.children;
        const optionsLength = options.length;

        if (event.key === 'ArrowDown') {
            if (this.activeIndex < optionsLength - 1) {
                this.activeIndex++;
                this.highlightOption();
            }
            event.preventDefault();
        } else if (event.key === 'ArrowUp') {
            if (this.activeIndex > 0) {
                this.activeIndex--;
                this.highlightOption();
            }
            event.preventDefault();
        } else if (event.key === 'Enter') {
            if (this.activeIndex >= 0 && this.activeIndex < optionsLength) {
                const activeOption = options[this.activeIndex] as HTMLElement;
                if (activeOption) {
                    const option = this.filteredList[this.activeIndex];
                    this.onSelectOption(option);
                }
                event.preventDefault(); // Prevent form submission
            }
        } else if (event.key === 'Escape') {
            if (this.isDropdownVisible) {
                this.closeDropdown();
                event.preventDefault();
            }
        } else {
            // Any other key (letters, digits, backspace, etc.)
            if (!this.allowFreeText) {
                // Don't let browser modify input value
                event.preventDefault();
                return;
            }
            // If free text allowed, `input` listener handles search & clearing.
        }
    }

    private highlightOption() {
        const options = this.dropdownElement.children;
        for (let i = 0; i < options.length; i++) {
            const optionElement = options[i] as HTMLElement;
            if (i === this.activeIndex) {
                this.renderer.addClass(optionElement, 'active');
                this.renderer.setStyle(optionElement, 'background-color', '#ECFFFE');
                optionElement.scrollIntoView({ block: 'nearest' });
            } else {
                this.renderer.removeClass(optionElement, 'active');
                this.renderer.setStyle(optionElement, 'background-color', '#fff');
            }
        }
    }

    private onSelectOption(option: any) {
        if (this._zefixTracked) {
            this.renderer.addClass(this.inputWrapperElement, 'zefix-modified');
            this.zefixModified.emit();
        }
        this.renderer.setStyle(this.clearButton, 'display', 'block');
        if (this.multiple) {
            const key = this.returnKey;
            const alreadySelected = this.selectedOptions.some((sel) => sel[key] === option[key]);
            if (!alreadySelected) {
                this.selectedOptions.push(option);
            } else {
                this.selectedOptions = this.selectedOptions.filter((sel) => sel[key] !== option[key]);
            }
            this.updateSelectedItems();
            this.renderer.setProperty(this.el.nativeElement, 'value', ''); // Clear input for further selection
            this.onChange(this.selectedOptions.map((opt) => (this.returnObject ? opt : opt[key])));
            this.selectionChange.emit(option);
        } else {
            const mainText = this.optionKeys
                .map((key) => getNestedValue(option, key))
                .filter(Boolean)
                .join(' - ');

            const extraText =
                this.extraLabel.length > 0
                    ? this.extraLabel
                        .map((key) => getNestedValue(option, key))
                        .filter(Boolean)
                        .join(' - ')
                    : '';

            const displayText = extraText ? `${mainText} (${extraText})` : mainText;
            const returnValue = this.returnObject ? option : option[this.returnKey];

            this.renderer.setProperty(this.el.nativeElement, 'value', displayText);

            // Update badge overlay after selection if badgeStyleDropdown is enabled
            if (this.badgeStyleDropdown) {
                this.updateBadgeOverlay(displayText);
            }

            this.closeDropdown();
            this._committedValue = returnValue;
            this.onChange(returnValue);
            this.selectionChange.emit(returnValue);
        }

        this.highlightSelectedOptions();
        this.onTouched();
    }

    private updateDisplay() {
        this.renderer.setStyle(this.clearButton, 'display', 'block');
        if (!this.multiple) {
            const displayText = this.selectedOptions
                .map((option) => {
                    const mainText = this.optionKeys
                        .map((key) => getNestedValue(option, key))
                        .filter(Boolean)
                        .join(' - ');

                    const extraText =
                        this.extraLabel.length > 0
                            ? this.extraLabel
                                .map((key) => getNestedValue(option, key))
                                .filter(Boolean)
                                .join(' - ')
                            : '';

                    return extraText ? `${mainText} (${extraText})` : mainText;
                })
                .join(', ');
            this.renderer.setProperty(this.el.nativeElement, 'value', displayText);
        }

        this.selectedOptions = this.options?.filter((option) =>
            this.selectedOptions.some((selectedOption) => selectedOption[this.returnKey] === option[this.returnKey]),
        );

        const returnValue = this.selectedOptions.map((option) => (this.returnObject ? option : option[this.returnKey]));
        this.onChange(returnValue);
    }

    private updateSelectedItems() {
        // Clear the current selected items
        while (this.selectedItemsElement.firstChild) {
            this.selectedItemsElement.removeChild(this.selectedItemsElement.firstChild);
        }

        if (this.selectedOptions.length > 0) {
            this.renderer.setStyle(this.selectedItemsElement, 'display', 'block'); // Show the selected items list

            // Add each selected item to the list
            this.selectedOptions.forEach((option) => {
                const itemElement = this.renderer.createElement('li');
                this.renderer.addClass(itemElement, 'selectedItem');
                this.renderer.setStyle(itemElement, 'width', 'auto');
                this.renderer.setStyle(itemElement, 'margin-right', '4px');
                this.renderer.setStyle(itemElement, 'background', '#CEFFFE');
                this.renderer.setStyle(itemElement, 'color', '#145765');
                this.renderer.setStyle(itemElement, 'padding', '2px 6px');
                this.renderer.setStyle(itemElement, 'border-radius', '6px');
                this.renderer.setStyle(itemElement, 'text-align', 'center');
                const itemContent = this.renderer.createElement('span');
                this.renderer.addClass(itemContent, 'icon-wrapper');

                const mainText = this.optionKeys
                    .map((key) => getNestedValue(option, key))
                    .filter(Boolean)
                    .join(' - ');

                const extraText =
                    this.extraLabel.length > 0
                        ? this.extraLabel
                            .map((key) => getNestedValue(option, key))
                            .filter(Boolean)
                            .join(' - ')
                        : '';

                const displayText = extraText ? `${mainText} (${extraText})` : mainText;

                const textContent = this.renderer.createElement('span');
                const textNode = this.renderer.createText(displayText);

                const removeIcon = this.renderer.createElement('i');
                this.renderer.addClass(removeIcon, 'x-icon');
                this.renderer.setStyle(removeIcon, 'margin-left', '4px');
                this.renderer.setStyle(removeIcon, 'cursor', 'pointer');

                this.renderer.appendChild(itemContent, removeIcon);
                this.renderer.appendChild(textContent, textNode);
                this.renderer.appendChild(itemElement, textContent);
                this.renderer.appendChild(itemElement, itemContent);

                // Add click listener to remove item
                this.listeners.push(
                    this.renderer.listen(removeIcon, 'click', (event) => {
                        event.stopPropagation();
                        const index = this.selectedOptions.indexOf(option);
                        if (index > -1) {
                            this.selectedOptions.splice(index, 1); // Remove the option
                            this.updateDisplay();
                            this.updateSelectedItems(); // Update the selected items display
                            this.selectionChange.emit(option);
                        }
                    }),
                );

                if (this.draggable) {
                    this.addDragAndDropListeners(itemElement, option);
                }

                this.renderer.appendChild(this.selectedItemsElement, itemElement);
            });
        } else {
            this.renderer.setStyle(this.selectedItemsElement, 'display', 'none'); // Hide if no selected items
        }
    }

    private addDragAndDropListeners(itemElement: HTMLElement, option: any) {
        itemElement.setAttribute('draggable', 'true');

        this.listeners.push(
            this.renderer.listen(itemElement, 'dragstart', (event) => {
                event.dataTransfer?.setData('text/plain', JSON.stringify(option));
                event.dataTransfer?.setData('text/html', itemElement.outerHTML);
                itemElement.classList.add('dragging');
            }),
        );

        this.listeners.push(
            this.renderer.listen(itemElement, 'dragend', () => {
                itemElement.classList.remove('dragging');
            }),
        );

        this.listeners.push(
            this.renderer.listen(itemElement, 'dragover', (event) => {
                event.preventDefault();
                itemElement.classList.add('drag-over');
            }),
        );

        this.listeners.push(
            this.renderer.listen(itemElement, 'dragleave', () => {
                itemElement.classList.remove('drag-over');
            }),
        );

        this.listeners.push(
            this.renderer.listen(itemElement, 'drop', (event) => {
                event.preventDefault();
                itemElement.classList.remove('drag-over');
                const draggedOption = JSON.parse(event.dataTransfer?.getData('text/plain') || '{}');
                this.onDrop(option, draggedOption);
            }),
        );
    }

    private onDrop(targetOption: INameId, draggedOption: INameId) {
        const key = this.returnKey || 'id';
        const targetIndex = this.options.findIndex((option) => option[key] === (targetOption as any)[key]);
        const draggedIndex = this.options.findIndex((option) => option[key] === (draggedOption as any)[key]);

        if (targetIndex !== -1 && draggedIndex !== -1 && targetIndex !== draggedIndex) {
            this.options.splice(draggedIndex, 1);
            this.options.splice(targetIndex, 0, draggedOption);
            this.selectedOptions = this.options.filter((option) =>
                this.selectedOptions.some((selectedOption) => selectedOption[key] === option[key]),
            );

            this.updateDropdown(this.options);
            this.updateSelectedItems();

            this.highlightSelectedOptions();
            this.onChange(this.selectedOptions.map((opt) => (this.returnObject ? opt : opt[this.returnKey])));
        }
    }

    // Search buffer management
    private updateSearchBuffer(char: string) {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
            this.searchBuffer = '';
        }, 500); // Reset buffer after 500ms of inactivity

        this.searchBuffer += char.toLowerCase();

        // Find the first option that starts with the search buffer
        const matchIndex = this.options.findIndex((option) =>
            this.optionKeys.some((key) =>
                getNestedValue(option, key)?.toString().toLowerCase().startsWith(this.searchBuffer),
            ),
        );

        if (matchIndex !== -1) {
            this.activeIndex = matchIndex;
            this.highlightOption();
        }
    }

    private highlightFirstMatching(query: string) {
        const matchIndex = this.options.findIndex((option) =>
            this.optionKeys.some((key) => getNestedValue(option, key)?.toString().toLowerCase().startsWith(query)),
        );

        if (matchIndex !== -1) {
            this.activeIndex = matchIndex;
            this.highlightOption();
        }
    }

    private highlightSelectedOptions() {
        const options = this.dropdownElement.children;
        for (let i = 0; i < options.length; i++) {
            const optionElement = options[i] as HTMLElement;
            const currentOption = this.options[i];
            if (!currentOption) {
                continue;
            }
            let isSelected;
            if (this.returnKey) {
                isSelected = this.selectedOptions.some(
                    (selectedOption) => selectedOption[this.returnKey] === currentOption[this.returnKey],
                );
            } else if (currentOption.id) {
                isSelected = this.selectedOptions.some((selectedOption) => selectedOption.id === currentOption.id);
            } else {
                const optionText = optionElement.textContent;
                isSelected = this.selectedOptions.some(
                    (option) =>
                        this.optionKeys
                            .map((key) => getNestedValue(option, key))
                            .filter(Boolean)
                            .join(' - ') === optionText,
                );
            }
            if (isSelected) {
                this.renderer.addClass(optionElement, 'highlight-selected');
            } else {
                this.renderer.removeClass(optionElement, 'highlight-selected');
            }
        }
    }
}

const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((acc, key) => acc && acc[key], obj);
};